/**
 * Smart Enrichment & Activity: what the directory has been doing, counted
 * and listed, never asserted.
 *
 * The tab this replaces showed four counters computed off the roster — one
 * of them counting a source string the server never emits — and a table of
 * demo-only "suggested contacts" whose Create and Dismiss buttons had no
 * handlers. Everything on this surface is read from the activity log the
 * backend writes on every capture, merge, lookup, edit and list change, or
 * from the enrichment summary it counts on request. The copy states what
 * runs automatically in THIS build and nothing more: capture on every
 * message and deal, dedup after every capture, external lookups only when a
 * person asks.
 *
 * Founder's decision, 4 Nov 2026: there is no duplicate queue and no
 * confirmation step. Duplicates are merged by the dedup pass on its own and
 * every merge is on the merge log with its Undo; an entry is flagged only when
 * the engine is unsure of it, and the panel below lists those with the reason.
 */
import { useState } from 'react'
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import clsx from 'clsx'
import { Check, ExternalLink, Loader2, RefreshCw, Sparkles, Wand2 } from 'lucide-react'

import { crmErrorDetail, crmService, type CrmContact } from '@/services/crm'
import MergeLog from './MergeLog'
import { ActivityEntry } from './ProfileSections'
import { FEED_FILTERS, captureSentence, dedupSentence, formatStamp, readFeedPage, relativeTime } from './crmFields'

const BUTTON =
  'inline-flex items-center gap-1.5 rounded-md border border-amber-400/15 px-2.5 py-1 text-[11px] text-stone-300 hover:border-amber-400/30 hover:text-stone-100 focus-visible:outline focus-visible:outline-1 focus-visible:outline-amber-400/60 disabled:cursor-not-allowed disabled:opacity-50'

function StatCell({ label, value, sub }: { label: string; value: string | number; sub?: string }) {
  return (
    <div className="metric-cell">
      <div className="metric-cell-label">{label}</div>
      <div className="metric-cell-value">{value}</div>
      {sub ? <div className="text-[9px] text-stone-600">{sub}</div> : null}
    </div>
  )
}

export default function ActivityView({ contacts, onOpenContact }: { contacts: CrmContact[]; onOpenContact: (id: string) => void }) {
  const queryClient = useQueryClient()
  const [filter, setFilter] = useState(0)
  const [dedupNote, setDedupNote] = useState<string | null>(null)
  const [captureNote, setCaptureNote] = useState<string | null>(null)
  const [confirmNote, setConfirmNote] = useState<string | null>(null)
  const kinds = FEED_FILTERS[filter].kinds

  const summary = useQuery({ queryKey: ['crm', 'enrichment', 'summary'], queryFn: () => crmService.getEnrichmentSummary() })

  const feed = useInfiniteQuery({
    queryKey: ['crm', 'activity', { kinds }],
    initialPageParam: undefined as string | undefined,
    queryFn: ({ pageParam }) => readFeedPage(kinds, pageParam),
    getNextPageParam: (last) => (last.hasMore ? (last.items[last.items.length - 1]?.createdAt ?? undefined) : undefined),
  })

  const invalidateAll = () => {
    queryClient.invalidateQueries({ queryKey: ['crm', 'contacts'] })
    queryClient.invalidateQueries({ queryKey: ['crm', 'lists'] })
    queryClient.invalidateQueries({ queryKey: ['crm', 'activity'] })
    queryClient.invalidateQueries({ queryKey: ['crm', 'enrichment'] })
    queryClient.invalidateQueries({ queryKey: ['crm', 'duplicates'] })
    queryClient.invalidateQueries({ queryKey: ['crm', 'merges'] })
  }

  const dedupe = useMutation({
    mutationFn: () => crmService.dedupe(),
    onSuccess: (s) => {
      setDedupNote(dedupSentence(s))
      invalidateAll()
    },
    onError: (err) => setDedupNote(crmErrorDetail(err, 'The dedup pass could not be run.')),
  })

  const capture = useMutation({
    mutationFn: () => crmService.captureFromDeals(),
    onSuccess: (s) => {
      setCaptureNote(captureSentence(s))
      invalidateAll()
    },
    onError: (err) => setCaptureNote(crmErrorDetail(err, 'The sweep could not be run.')),
  })

  const confirm = useMutation({
    mutationFn: (id: string) => crmService.confirmContact(id),
    onSuccess: () => {
      setConfirmNote(null)
      queryClient.invalidateQueries({ queryKey: ['crm', 'contacts'] })
      queryClient.invalidateQueries({ queryKey: ['crm', 'activity'] })
      queryClient.invalidateQueries({ queryKey: ['crm', 'enrichment', 'summary'] })
    },
    onError: (err) => setConfirmNote(crmErrorDetail(err, 'This contact could not be marked checked.')),
  })

  const needsReview = contacts.filter((c) => c.needsReview && !c.retired)
  const items = feed.data?.pages.flatMap((p) => p.items) ?? []

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-3 overflow-hidden p-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0 max-w-3xl">
          <div className="flex items-center gap-1.5">
            <Sparkles size={12} className="text-amber-400" />
            <span className="terminal-kicker">Smart Enrichment &amp; Activity</span>
          </div>
          <p className="mt-1 text-[11px] leading-relaxed text-stone-400">
            Capture runs on every message and every deal automatically, and the dedup pass runs after every capture and merges
            duplicates on its own — each merge is in the merge log with an Undo. Nothing waits for a confirmation; an entry is
            flagged only when the engine is unsure of it. External lookups run only when a person asks for one — nothing here
            reads the web on its own. Every row below is something the platform recorded, newest first.
          </p>
        </div>
        <div className="flex flex-col items-end gap-1">
          <div className="flex items-center gap-1.5">
            <button type="button" onClick={() => dedupe.mutate()} disabled={dedupe.isPending} className={BUTTON}>
              <Wand2 size={11} className={dedupe.isPending ? 'motion-safe:animate-pulse' : undefined} />
              {dedupe.isPending ? 'Running dedup…' : 'Run dedup now'}
            </button>
            <button
              type="button"
              onClick={() => capture.mutate()}
              disabled={capture.isPending}
              title="Capture runs on every message and deal automatically; this re-reads everything already on the books and runs dedup."
              className={BUTTON}
            >
              <RefreshCw size={11} className={capture.isPending ? 'motion-safe:animate-spin' : undefined} />
              {capture.isPending ? 'Reading deals…' : 'Re-read deals'}
            </button>
          </div>
          {dedupNote ? <div className="text-[10px] text-stone-400">{dedupNote}</div> : null}
          {captureNote ? <div className="text-[10px] text-stone-400">{captureNote}</div> : null}
        </div>
      </div>

      <div className="grid grid-cols-3 gap-2 lg:grid-cols-6">
        {summary.isPending ? (
          <div className="col-span-3 flex items-center gap-1.5 text-[10px] text-stone-500 lg:col-span-6">
            <Loader2 size={10} className="motion-safe:animate-spin" /> Counting…
          </div>
        ) : summary.isError ? (
          <div className="col-span-3 text-[10px] text-red-300 lg:col-span-6">
            {crmErrorDetail(summary.error, 'The enrichment summary could not be read.')}
          </div>
        ) : (
          <>
            <StatCell label="Looked up" value={summary.data.lookedUp} sub="parties with an external lookup on record" />
            <StatCell label="Never looked up" value={summary.data.neverLookedUp} sub="parties nobody has asked about" />
            <StatCell
              label="Last lookup"
              value={summary.data.lastRunAt ? relativeTime(summary.data.lastRunAt) : 'Never'}
              sub={summary.data.lastRunAt ? formatStamp(summary.data.lastRunAt) : 'no lookup has run'}
            />
            <StatCell label="Lookups (24h)" value={summary.data.runsLast24h} sub="on request" />
            <StatCell label="Captures (24h)" value={summary.data.capturesLast24h} sub="messages and deals read" />
            <StatCell label="Merges (24h)" value={summary.data.mergesLast24h} sub="by the dedup pass" />
          </>
        )}
      </div>

      <div className="grid min-h-0 flex-1 grid-cols-12 gap-3">
        <div className="panel col-span-12 flex min-h-0 flex-col lg:col-span-8">
          <div className="panel-header">
            <span className="panel-header-title">Activity feed</span>
            <div className="flex items-center gap-1" role="tablist" aria-label="Activity kind">
              {FEED_FILTERS.map((f, i) => (
                <button
                  key={f.label}
                  type="button"
                  role="tab"
                  aria-selected={filter === i}
                  onClick={() => setFilter(i)}
                  className={clsx(
                    'rounded border px-1.5 py-0.5 font-mono text-[9px] uppercase tracking-wider focus-visible:outline focus-visible:outline-1 focus-visible:outline-amber-400/60',
                    filter === i ? 'border-amber-400/40 bg-amber-400/[0.1] text-amber-200' : 'border-transparent text-stone-500 hover:text-stone-200',
                  )}
                >
                  {f.label}
                </button>
              ))}
            </div>
          </div>
          <div className="panel-body min-h-0 flex-1 overflow-y-auto scrollbar-thin">
            {feed.isPending ? (
              <div className="flex items-center gap-1.5 text-[10px] text-stone-500">
                <Loader2 size={10} className="motion-safe:animate-spin" /> Reading the log…
              </div>
            ) : feed.isError ? (
              <div className="text-[10px] text-red-300">{crmErrorDetail(feed.error, 'The activity log could not be read.')}</div>
            ) : items.length === 0 ? (
              <div className="text-[11px] italic text-stone-500">Nothing has happened yet — activity appears here as emails arrive.</div>
            ) : (
              <>
                <ol className="space-y-1.5 border-l border-amber-400/25">
                  {items.map((entry) => (
                    <ActivityEntry key={entry.id} entry={entry} onOpenContact={onOpenContact} />
                  ))}
                </ol>
                {feed.hasNextPage ? (
                  <div className="mt-2 flex justify-center">
                    <button type="button" onClick={() => feed.fetchNextPage()} disabled={feed.isFetchingNextPage} className={BUTTON}>
                      {feed.isFetchingNextPage ? <Loader2 size={10} className="motion-safe:animate-spin" /> : null}
                      {feed.isFetchingNextPage ? 'Loading…' : 'Load more'}
                    </button>
                  </div>
                ) : null}
              </>
            )}
          </div>
        </div>

        <div className="col-span-12 flex min-h-0 flex-col gap-3 lg:col-span-4">
          <MergeLog onOpenContact={onOpenContact} />
          <div className="panel flex min-h-0 flex-col" aria-label="Unsure entries">
            <div className="panel-header">
              <span className="panel-header-title">Unsure entries</span>
              <span className="font-mono text-[9px] text-stone-500">{needsReview.length}</span>
            </div>
            <div className="panel-body min-h-0 flex-1 overflow-y-auto scrollbar-thin">
              {confirmNote ? <div className="mb-1.5 text-[10px] text-red-300">{confirmNote}</div> : null}
              {needsReview.length === 0 ? (
                <div className="text-[11px] italic text-stone-500">
                  The engine is sure of every entry. Contacts are added and merged without review.
                </div>
              ) : (
                <ul className="space-y-1">
                  {needsReview.map((c) => (
                    <li key={c.id} className="flex items-center justify-between gap-2 rounded border border-amber-400/10 bg-black/20 px-2 py-1.5">
                      <div className="min-w-0">
                        <div className="truncate text-[11px] text-stone-200">{c.displayName}</div>
                        <div className="truncate text-[9px] text-stone-500">
                          {[c.companyType, c.company].filter(Boolean).join(' · ')}
                        </div>
                        {c.reviewReason ? (
                          <div className="text-[9px] leading-snug text-amber-200/80" title={c.reviewReason}>
                            {c.reviewReason}
                          </div>
                        ) : null}
                      </div>
                      <div className="flex shrink-0 items-center gap-1">
                        <button
                          type="button"
                          disabled={confirm.isPending}
                          onClick={() => confirm.mutate(c.id)}
                          aria-label={`Mark ${c.displayName} checked`}
                          className={BUTTON}
                        >
                          <Check size={10} /> Mark checked
                        </button>
                        <button type="button" onClick={() => onOpenContact(c.id)} aria-label={`Open ${c.displayName}`} className={BUTTON}>
                          <ExternalLink size={10} /> Open
                        </button>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
